/**
 * Leviathan Web - Inventory Form Page
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Create and edit inventory products with pricing and stock management
 */

import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/utils/api';

import { API_URL } from '@/config/api';
import { readApiFailure } from '@/utils/formErrors';
import {
  TAX_CLASS_OPTIONS,
  taxClassLabel,
  type TaxClassSuggestion,
} from '@/constants/productTaxClass';

interface FormData {
  name: string;
  sku: string;
  category: string;
  taxClass: string;
  description: string;
  productType: string;
  grade: string;
  unit: string;
  /**
   * Held as strings so "not set" (empty) stays distinguishable from "zero".
   * A product with no price is a real state — a service line, or one priced
   * per contract — and it is not the same as a product that costs nothing.
   */
  costPerUnit: string;
  pricePerUnit: string;
  currentStock: number;
  reorderPoint: number;
  reorderQuantity: number;
  isActive: boolean;
  isTracked: boolean;
}

interface ProductTypeConfig {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface ProductGradeConfig {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

const CATEGORIES = [
  { value: 'FUEL', label: 'Fuel' },
  { value: 'ADDITIVE', label: 'Additive' },
  { value: 'LUBRICANT', label: 'Lubricant' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'OTHER', label: 'Other' },
];

const UNITS = ['gallon', 'liter', 'barrel', 'quart', 'pint', 'each', 'case', 'drum'];

export default function InventoryFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productTypes, setProductTypes] = useState<ProductTypeConfig[]>([]);
  const [productGrades, setProductGrades] = useState<ProductGradeConfig[]>([]);
  /**
   * A guess at the tax class from the product name, offered while the user
   * types. Never applied automatically — see the "Use it" button. A wrong tax
   * class that looks confirmed is worse than an empty one that is visibly
   * missing.
   */
  const [suggestion, setSuggestion] = useState<TaxClassSuggestion | null>(null);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    sku: '',
    category: 'FUEL',
    taxClass: '',
    description: '',
    productType: '',
    grade: '',
    unit: 'gallon',
    costPerUnit: '',
    pricePerUnit: '',
    currentStock: 0,
    reorderPoint: 0,
    reorderQuantity: 0,
    isActive: true,
    isTracked: true,
  });

  useEffect(() => {
    fetchConfiguration();
    if (isEditMode) {
      fetchProduct();
    }
  }, [id]);

  /**
   * Ask the API to guess a tax class from the product name.
   *
   * Only while the field is EMPTY — once a human has chosen, a suggestion
   * hovering underneath would imply the choice is provisional. Debounced,
   * because it fires on every keystroke.
   */
  useEffect(() => {
    if (formData.taxClass || !formData.name.trim()) {
      setSuggestion(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          name: formData.name,
          category: formData.category,
          ...(formData.productType ? { productType: formData.productType } : {}),
        });
        const response = await apiFetch(`${API_URL}/api/inventory/tax-class/suggest?${params}`);
        if (response.ok) {
          const data = await response.json();
          setSuggestion(data.suggestion);
        }
      } catch {
        // A failed suggestion is not worth surfacing: the field still works,
        // and it is already flagged as unset by the warning above it.
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.name, formData.category, formData.productType, formData.taxClass]);

  const fetchConfiguration = async () => {
    try {
      const [typesRes, gradesRes] = await Promise.all([
        apiFetch(`${API_URL}/api/configuration/product-types?isActive=true`),
        apiFetch(`${API_URL}/api/configuration/product-grades?isActive=true`),
      ]);

      if (typesRes.ok) {
        const data = await typesRes.json();
        setProductTypes(data.types || []);
      }
      if (gradesRes.ok) {
        const data = await gradesRes.json();
        setProductGrades(data.grades || []);
      }
    } catch (error) {
      console.error('Failed to fetch configuration:', error);
    }
  };

  const fetchProduct = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/inventory/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const item = data.item || data;
        setFormData({
          name: item.name || '',
          sku: item.sku || '',
          category: item.category || 'FUEL',
          taxClass: item.taxClass || '',
          description: item.description || '',
          productType: item.productType || '',
          grade: item.grade || '',
          unit: item.unit || 'gallon',
          costPerUnit: item.costPerUnit == null ? '' : String(item.costPerUnit),
          pricePerUnit: item.pricePerUnit == null ? '' : String(item.pricePerUnit),
          currentStock: item.currentStock || 0,
          reorderPoint: item.reorderPoint || 0,
          reorderQuantity: item.reorderQuantity || 0,
          isActive: item.isActive ?? true,
          isTracked: item.isTracked ?? true,
        });
      } else {
        setError('Product not found');
      }
    } catch (error) {
      setError('Failed to load product');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Product name is required');
      return;
    }

    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/inventory/${id}` : `${API_URL}/api/inventory`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        navigate('/inventory');
      } else {
        /**
         * The API failure body is { success, error: { code, message, details } }
         * — there is no top-level `message`. `data.message || data.error`
         * therefore put the error OBJECT into React state, and rendering an
         * object as a child made React unmount the whole application to a
         * white page. `readApiFailure` always returns a string, and carries
         * the per-field issues alongside it.
         */
        const failure = await readApiFailure(response, 'Failed to save product');
        setError(failure.message);
      }
    } catch (error) {
      setError('Failed to save product');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading product...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Product' : 'New Product'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify product details' : 'Add a new inventory item'}
              </p>
            </div>
            <button
              onClick={() => navigate('/inventory')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider transition-colors"
            >
              &larr; Back to Inventory
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Panel title="Product Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Product Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g., Regular Unleaded Gasoline"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                SKU {!isEditMode && '(auto-generated if blank)'}
              </label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => updateField('sku', e.target.value)}
                placeholder="e.g., FUEL-REG-87"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => updateField('category', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-text-tertiary font-mono">
                How this product is grouped in inventory. It does{' '}
                <strong className="text-amber">not</strong> decide how it is taxed — see Tax Class
                below.
              </p>
            </div>

            {/* TAX CLASS.
                Sits immediately after Category and at full width, because the
                two names are confusingly similar and a user will otherwise
                assume setting Category is enough. It is not: the two enums
                share only the value OTHER, so a FUEL product with no tax class
                matches no fuel tax at all and every invoice line for it is
                silently untaxed. */}
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Tax Class *
              </label>
              <select
                value={formData.taxClass}
                onChange={(e) => {
                  updateField('taxClass', e.target.value);
                  // Choosing by hand supersedes the suggestion. Keeping it on
                  // screen afterwards would imply the value is still a guess.
                  setSuggestion(null);
                }}
                className={`w-full bg-background border px-4 py-2 text-text-primary font-mono text-sm focus:ring-1 focus:ring-amber ${
                  formData.taxClass ? 'border-panel-border focus:border-amber' : 'border-error-red'
                }`}
              >
                <option value="">— Not set —</option>
                {TAX_CLASS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {formData.taxClass ? (
                <p className="mt-1 text-[11px] text-text-tertiary font-mono">
                  {TAX_CLASS_OPTIONS.find((o) => o.value === formData.taxClass)?.description}
                </p>
              ) : (
                <div className="mt-2 flex items-start gap-2 border border-error-red/50 bg-error-red/10 p-3">
                  <AlertTriangle className="w-4 h-4 text-error-red flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-text-secondary font-mono leading-relaxed">
                    <strong className="text-error-red">No tax class set.</strong> Fuel taxes are
                    matched by tax class, not by category — so this product will be{' '}
                    <strong className="text-error-red">invoiced with no fuel tax at all</strong>,
                    silently. If it genuinely is not a fuel, choose{' '}
                    <em>Other / Not fuel</em> so the decision is on the record.
                  </p>
                </div>
              )}

              {/* A suggestion is offered, never applied. A wrong tax class that
                  looks confirmed is worse than an empty one that is visibly
                  missing — the empty one shows up on every warning surface. */}
              {suggestion && !formData.taxClass && (
                <div className="mt-2 flex items-start justify-between gap-3 border border-amber/40 bg-amber/5 p-3">
                  <div className="min-w-0">
                    <p className="text-[11px] text-amber font-mono">
                      Suggested: <strong>{taxClassLabel(suggestion.taxClass)}</strong>
                      <span className="ml-2 text-text-tertiary">
                        ({suggestion.confidence.toLowerCase()} confidence)
                      </span>
                    </p>
                    <p className="text-[11px] text-text-tertiary font-mono mt-0.5">
                      {suggestion.reason} Check it before accepting — this decides what tax the
                      product attracts.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      updateField('taxClass', suggestion.taxClass);
                      setSuggestion(null);
                    }}
                    className="flex-shrink-0 border border-amber text-amber px-3 py-1 text-[11px] font-mono hover:bg-amber/10"
                  >
                    Use it
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Product Type
              </label>
              <select
                value={formData.productType}
                onChange={(e) => updateField('productType', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                <option value="">Select Product Type</option>
                {productTypes.map((type) => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Grade
              </label>
              <select
                value={formData.grade}
                onChange={(e) => updateField('grade', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                <option value="">Select Grade</option>
                {productGrades.map((grade) => (
                  <option key={grade.id} value={grade.name}>
                    {grade.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={3}
                placeholder="Product description..."
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Unit of Measure */}
        <Panel title="Unit of Measure">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Unit of Measure
              </label>
              <select
                value={formData.unit}
                onChange={(e) => updateField('unit', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Pricing.
            These two columns have existed on InventoryItem and been accepted by
            the API since the module was written; the form never offered them.
            A product created through the UI therefore had pricePerUnit = null,
            and the order form — which fills a line's unit price from the
            product — then posted a null unit price and the ORDER was rejected.
            The gap was in the product screen; the failure surfaced two screens
            later, on the order. */}
        <Panel title="Pricing">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Price Per Unit
              </label>
              <input
                type="number"
                inputMode="decimal"
                value={formData.pricePerUnit}
                onChange={(e) => updateField('pricePerUnit', e.target.value)}
                min="0"
                step="0.0001"
                placeholder="e.g. 3.2500"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
              <p className="text-text-tertiary text-xs font-mono mt-2">
                What you charge, per {formData.unit}. Used as the default line price when this
                product is added to an order. Leave blank if it is always priced per contract —
                the order will then ask for a price.
              </p>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Cost Per Unit
              </label>
              <input
                type="number"
                inputMode="decimal"
                value={formData.costPerUnit}
                onChange={(e) => updateField('costPerUnit', e.target.value)}
                min="0"
                step="0.0001"
                placeholder="e.g. 2.9100"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
              <p className="text-text-tertiary text-xs font-mono mt-2">
                What you pay, per {formData.unit}. Used for margin reporting.
              </p>
            </div>
          </div>
        </Panel>

        {/* Stock */}
        <Panel title="Stock Management">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Current Stock
              </label>
              <input
                type="number"
                value={formData.currentStock || ''}
                onChange={(e) => updateField('currentStock', parseFloat(e.target.value) || 0)}
                min="0"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Reorder Point
              </label>
              <input
                type="number"
                value={formData.reorderPoint || ''}
                onChange={(e) => updateField('reorderPoint', parseFloat(e.target.value) || 0)}
                min="0"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Reorder Quantity
              </label>
              <input
                type="number"
                value={formData.reorderQuantity || ''}
                onChange={(e) => updateField('reorderQuantity', parseFloat(e.target.value) || 0)}
                min="0"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>

          <div className="flex space-x-6 mt-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isTracked}
                onChange={(e) => updateField('isTracked', e.target.checked)}
                className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
              />
              <span className="text-text-secondary font-mono text-sm">Track Inventory</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
                className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
              />
              <span className="text-text-secondary font-mono text-sm">Active</span>
            </label>
          </div>
        </Panel>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/inventory')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Product' : 'Create Product'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
